import { useState, useEffect, useCallback } from 'react';
import { EventSummary } from '@/components/EventSummary';
import { summaryText } from '@/lib/eventSummary';
import { SchoolSelector } from '@/components/SchoolSelector';
import { eventsApi, roomsApi, schoolsApi } from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { cn, utcDate } from '@/lib/utils';
import { format, isToday, isYesterday } from 'date-fns';
import { sv } from 'date-fns/locale';
import {
  AlertTriangle, CheckCircle, XCircle, Clock, User, MessageSquare,
  Flame, SprayCan, Search, Shield, WifiOff, Filter, DoorOpen,
  Building2, ChevronDown
} from 'lucide-react';

const typeConfig = {
  vape: { label: 'VAPE', icon: Flame, bg: 'bg-red-500', ring: 'ring-red-200', text: 'text-red-700', light: 'bg-red-50' },
  suspect: { label: 'MISSTANKE', icon: AlertTriangle, bg: 'bg-amber-500', ring: 'ring-amber-200', text: 'text-amber-700', light: 'bg-amber-50' },
  offline: { label: 'OFFLINE', icon: WifiOff, bg: 'bg-slate-400', ring: 'ring-slate-200', text: 'text-slate-600', light: 'bg-slate-50' },
  test_alarm: { label: 'TEST', icon: Shield, bg: 'bg-blue-500', ring: 'ring-blue-200', text: 'text-blue-700', light: 'bg-blue-50' },
};

const statusConfig = {
  open: { label: 'Öppen', icon: AlertTriangle, color: 'text-red-600' },
  acknowledged: { label: 'Bekräftad', icon: CheckCircle, color: 'text-amber-600' },
  resolved: { label: 'Löst', icon: CheckCircle, color: 'text-emerald-600' },
  false_positive: { label: 'Falskt positiv', icon: XCircle, color: 'text-slate-500' },
};

function formatDateGroup(dateStr) {
  const d = utcDate(dateStr);
  if (isToday(d)) return 'Idag';
  if (isYesterday(d)) return 'Igår';
  return format(d, 'd MMMM yyyy', { locale: sv });
}

function TimelineEntry({ event }) {
  const type = typeConfig[event.event_type] || typeConfig.suspect;
  const status = statusConfig[event.status] || statusConfig.open;
  const TypeIcon = type.icon;
  const StatusIcon = status.icon;
  const isTest = event.is_test;

  // Build action timeline from event fields
  const actions = [];
  actions.push({
    time: event.created_at,
    label: isTest ? 'Testalarm utlöst' : `${type.label} detekterad`,
    detail: summaryText(event),
    icon: TypeIcon,
    color: type.text,
  });
  if (event.is_acknowledged && event.acknowledged_at) {
    actions.push({
      time: event.acknowledged_at,
      label: 'Bekräftad',
      detail: event.acknowledged_by ? `av ${event.acknowledged_by}` : '',
      icon: CheckCircle,
      color: 'text-amber-600',
    });
  }
  if (event.vape_confirmed && event.confirmed_at) {
    actions.push({
      time: event.confirmed_at,
      label: 'Vape bekräftad',
      detail: event.confirmed_by ? `av ${event.confirmed_by}` : '',
      icon: Flame,
      color: 'text-red-600',
    });
  }
  if (event.spray_classified) {
    actions.push({
      time: event.false_positive_at || event.created_at,
      label: 'Markerad som spray',
      detail: event.false_positive_by ? `av ${event.false_positive_by}` : '',
      icon: SprayCan,
      color: 'text-purple-600',
    });
  }
  if (event.nothing_found) {
    actions.push({
      time: event.resolved_at || event.created_at,
      label: 'Inget hittades',
      detail: event.resolved_by ? `av ${event.resolved_by}` : '',
      icon: Search,
      color: 'text-slate-500',
    });
  }
  if (event.is_false_positive && !event.spray_classified && event.false_positive_at) {
    actions.push({
      time: event.false_positive_at,
      label: 'Markerad falskt positiv',
      detail: event.false_positive_by ? `av ${event.false_positive_by}` : '',
      icon: XCircle,
      color: 'text-slate-500',
    });
  }
  if (event.status === 'resolved' && !event.vape_confirmed && !event.nothing_found && event.resolved_at) {
    actions.push({
      time: event.resolved_at,
      label: 'Löst',
      detail: event.resolution_notes || (event.resolved_by ? `av ${event.resolved_by}` : ''),
      icon: CheckCircle,
      color: 'text-emerald-600',
    });
  }

  return (
    <div className={cn("relative pl-8 pb-6 group")} data-testid={`timeline-event-${event.id}`}>
      {/* Vertical line */}
      <div className="absolute left-[13px] top-6 bottom-0 w-px bg-slate-200 group-last:hidden" />

      {/* Dot */}
      <div className={cn("absolute left-0 top-1 h-7 w-7 rounded-full flex items-center justify-center ring-4 ring-white", type.bg)}>
        <TypeIcon className="h-3.5 w-3.5 text-white" />
      </div>

      {/* Content */}
      <div className={cn("rounded-lg border p-4", type.light, 'border-slate-200')}>
        {/* Header */}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <span className={cn("text-xs font-bold uppercase tracking-wider px-1.5 py-0.5 rounded text-white", type.bg)}>
              {type.label}
            </span>
            <span className={cn("text-xs font-semibold", status.color)}>
              {status.label}
            </span>
            {event.severity && (
              <span className={cn(
                "text-[10px] font-bold px-1.5 py-0.5 rounded",
                event.severity === 'high' ? 'bg-red-600 text-white' :
                event.severity === 'medium' ? 'bg-orange-500 text-white' : 'bg-slate-400 text-white'
              )}>
                {event.severity === 'high' ? 'HÖG' : event.severity === 'medium' ? 'MEDEL' : 'LÅG'}
              </span>
            )}
            {isTest && <span className="text-[10px] font-semibold text-blue-600 bg-blue-100 px-1.5 py-0.5 rounded">TEST</span>}
          </div>
          <span className="text-xs text-slate-400 font-ibm">
            {format(utcDate(event.created_at), 'HH:mm:ss')}
          </span>
        </div>

        {/* Device & Room */}
        <p className="text-sm font-semibold text-slate-800 font-ibm">
          {event.device_name || 'Sensor'} — {event.room_name || 'Rum'}
        </p>
        {event.school_name && (
          <p className="text-xs text-slate-400 font-ibm">{event.school_name}</p>
        )}

        {/* Score */}
        {event.score > 0 && (
          <div className="mt-2 flex items-center gap-2">
            <div className="h-1.5 flex-1 bg-slate-200 rounded-full overflow-hidden">
              <div
                className={cn("h-full rounded-full", event.score >= 0.7 ? 'bg-red-500' : event.score >= 0.4 ? 'bg-amber-500' : 'bg-emerald-500')}
                style={{ width: `${Math.min(event.score * 100, 100)}%` }}
              />
            </div>
            <span className="text-xs font-mono text-slate-500">{(event.score * 100).toFixed(0)}%</span>
          </div>
        )}

        {/* Explanation */}
        <EventSummary event={event} showCertainty={false} className="mt-2" />

        {/* Action timeline */}
        {actions.length > 1 && (
          <div className="mt-3 pt-3 border-t border-slate-200/60 space-y-1.5">
            {actions.slice(1).map((a, i) => {
              const AIcon = a.icon;
              return (
                <div key={i} className="flex items-center gap-2 text-xs font-ibm">
                  <AIcon className={cn("h-3.5 w-3.5 shrink-0", a.color)} />
                  <span className="font-medium text-slate-700">{a.label}</span>
                  {a.detail && <span className="text-slate-400">{a.detail}</span>}
                  <span className="text-slate-300 ml-auto">{format(utcDate(a.time), 'HH:mm')}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default function AlarmHistoryPage() {
  const [events, setEvents] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRoom, setSelectedRoom] = useState('all');
  const [selectedSchool, setSelectedSchool] = useState('all');
  const [selectedType, setSelectedType] = useState('all');

  useEffect(() => {
    Promise.all([
      roomsApi.list().then(r => setRooms(r.data)),
      schoolsApi.list().then(r => setSchools(r.data)),
    ]).catch(() => {});
  }, []);

  const loadEvents = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (selectedType !== 'all') params.event_type = selectedType;
      if (selectedSchool !== 'all') params.school_id = selectedSchool;
      if (selectedRoom !== 'all') params.room_id = selectedRoom;
      const res = await eventsApi.list(params);
      setEvents(res.data);
    } catch {
      toast.error('Kunde inte ladda larmhistorik');
    } finally {
      setLoading(false);
    }
  }, [selectedRoom, selectedSchool, selectedType]);

  useEffect(() => { loadEvents(); }, [loadEvents]);

  // Group events by date
  const groupedByDate = {};
  events.forEach(e => {
    const key = format(utcDate(e.created_at), 'yyyy-MM-dd');
    if (!groupedByDate[key]) groupedByDate[key] = [];
    groupedByDate[key].push(e);
  });
  const sortedDates = Object.keys(groupedByDate).sort((a, b) => b.localeCompare(a));

  // Stats
  const vapeCount = events.filter(e => e.event_type === 'vape' && !e.is_test).length;
  const suspectCount = events.filter(e => e.event_type === 'suspect').length;
  const resolvedCount = events.filter(e => e.status === 'resolved' || e.status === 'false_positive').length;
  const avgResponseMin = (() => {
    const withAck = events.filter(e => e.acknowledged_at && e.created_at);
    if (!withAck.length) return null;
    const total = withAck.reduce((sum, e) => {
      return sum + (utcDate(e.acknowledged_at) - utcDate(e.created_at)) / 60000;
    }, 0);
    return Math.round(total / withAck.length);
  })();

  const filteredRooms = selectedSchool === 'all'
    ? rooms
    : rooms.filter(r => r.school_id === selectedSchool);

  return (
    <div className="p-6 md:p-10" data-testid="alarm-history-page">
      <div className="mb-6">
        <h1 className="font-chivo font-black text-2xl md:text-3xl text-navy tracking-tight">Larmhistorik</h1>
        <p className="text-slate-500 mt-1 font-ibm">Tidslinje med alla larm, kvitteringar och åtgärder</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <div className="bg-red-50 border border-red-100 rounded-lg p-3 text-center">
          <p className="text-2xl font-chivo font-black text-red-600">{vapeCount}</p>
          <p className="text-xs font-ibm text-red-400 uppercase tracking-wider">Vape-larm</p>
        </div>
        <div className="bg-amber-50 border border-amber-100 rounded-lg p-3 text-center">
          <p className="text-2xl font-chivo font-black text-amber-600">{suspectCount}</p>
          <p className="text-xs font-ibm text-amber-400 uppercase tracking-wider">Misstankar</p>
        </div>
        <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-3 text-center">
          <p className="text-2xl font-chivo font-black text-emerald-600">{resolvedCount}</p>
          <p className="text-xs font-ibm text-emerald-400 uppercase tracking-wider">Hanterade</p>
        </div>
        <div className="bg-slate-50 border border-slate-100 rounded-lg p-3 text-center">
          <p className="text-2xl font-chivo font-black text-slate-600">{avgResponseMin !== null ? `${avgResponseMin} min` : '—'}</p>
          <p className="text-xs font-ibm text-slate-400 uppercase tracking-wider">Snitt svarstid</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-6">
        <SchoolSelector
          schools={schools}
          value={selectedSchool}
          onChange={(v) => { setSelectedSchool(v); setSelectedRoom('all'); }}
          triggerClassName="w-48 bg-white"
        />

        <Select value={selectedRoom} onValueChange={setSelectedRoom}>
          <SelectTrigger className="w-52 bg-white" data-testid="history-room-filter">
            <DoorOpen className="h-4 w-4 mr-2 text-slate-400" />
            <SelectValue placeholder="Alla rum" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alla rum</SelectItem>
            {filteredRooms.map(r => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
          </SelectContent>
        </Select>

        <Select value={selectedType} onValueChange={setSelectedType}>
          <SelectTrigger className="w-44 bg-white" data-testid="history-type-filter">
            <Filter className="h-4 w-4 mr-2 text-slate-400" />
            <SelectValue placeholder="Alla typer" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alla typer</SelectItem>
            <SelectItem value="vape">Vape</SelectItem>
            <SelectItem value="suspect">Misstanke</SelectItem>
            <SelectItem value="offline">Offline</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Timeline */}
      {loading ? (
        <div className="flex items-center justify-center py-20 text-slate-400">
          <Clock className="h-5 w-5 animate-spin mr-2" /> Laddar historik...
        </div>
      ) : events.length === 0 ? (
        <div className="text-center py-20">
          <Shield className="h-12 w-12 mx-auto text-slate-300 mb-3" />
          <p className="text-slate-400 font-ibm">Inga larm hittades för vald filtrering</p>
        </div>
      ) : (
        <div className="space-y-8">
          {sortedDates.map(date => (
            <div key={date}>
              <div className="sticky top-0 z-10 bg-slate-50/95 backdrop-blur-sm py-2 mb-4">
                <h2 className="font-chivo font-bold text-sm text-slate-600 uppercase tracking-wider">
                  {formatDateGroup(groupedByDate[date][0].created_at)}
                  <span className="ml-2 text-slate-300 font-normal normal-case">
                    {groupedByDate[date].length} larm
                  </span>
                </h2>
              </div>
              <div>
                {groupedByDate[date].map(event => (
                  <TimelineEntry key={event.id} event={event} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
